import {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import LanguageContext from "@/context/language/LanguageContext";
import { useDashboardStore } from "@/stores/useDashboardStore";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../api/resources";
import useResponse from "../../../../../shared/hooks/useResponse";
import { imageList } from "../config";

const useNews = () => {
  const { handleCommonError } = useResponse();
  const { getData } = useAxiosPrivate();
  const { t } = useTranslation();

  const {
    setNewsScrollOffset,
    setNewsData,
    setIsDashboardLoading,
    setNewsError
  } = useDashboardStore();

  const { language } = useContext(LanguageContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const initialized = useRef(false);

  const preloadImages = useCallback(() => {
    imageList.forEach(({ imageName }) => {
      const img = new Image();
      img.src = `/img/news/${imageName}`;
    });
  }, []);

  const fetchedForLanguage = useRef<string | null>(null);

  const getNews = async () => {
    try {
      setIsLoading(true);

      preloadImages();
      const response = await getData(ApiResources.NewsItems);

      setNewsData(response.data);
      setNewsScrollOffset(0);
    } catch (error) {
      handleCommonError(error, t);
      setNewsError(true);
    } finally {
      initialized.current = true;

      setIsLoading(false);
      setIsDashboardLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (!language || fetchedForLanguage.current === language) return;

    fetchedForLanguage.current = language;
    setNewsError(false);
    getNews();
  }, [language, getNews]);

  return { isLoading, initialized };
};

export default useNews;
